import React from 'react';
import {
    Box,
    Button,
    Collapse,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiInformationOutline,
    mdiSecurity,
    mdiFormatSize,
    mdiChevronRight,
    mdiChevronDown,
} from '@mdi/js';
import i18next from 'i18next';
import styles from './DocPropsDialog.module.scss';
import type {
    IDocPropsDialogProps,
    IDocPropsTabProps,
    IDocumentProps,
} from '../../../types';

/**
 * Tab component.
 * @param param0 - IDocPropsTabProps
 * @returns Tab content in the accordance with tab's value and index.
 */
function DocPropsTab({ children, value, index, ...other }: IDocPropsTabProps) {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`docProps-tabpanel-${index}`}
            className={styles.docPropsTab}
            {...other}
        >
            {value === index && <Box sx={{ px: 1 }}>{children}</Box>}
        </Box>
    );
}

// List of tabs with icons.
const tabList: Record<keyof IDocumentProps, string> = {
    Description: mdiInformationOutline,
    Security: mdiSecurity,
    Fonts: mdiFormatSize,
};

/**
 * Component that shows document's properties.
 */
const DocPropsDialog: React.FC<IDocPropsDialogProps> = ({ onClose, documentProps }) => {
    const [value, setValue] = React.useState<number>(0);
    const [isCollapseOpen, setIsCollapseOpen] = React.useState<Record<number, boolean>>({});

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const handleFontsCollapseClick = (index: number) => {
        setIsCollapseOpen({
            ...isCollapseOpen,
            [index]: isCollapseOpen[index] ? !isCollapseOpen[index] : true,
        });
    };

    const createDocPropsTabsContent = () => {
        return Object.keys(documentProps).map((tab, index) => (
            <DocPropsTab key={tab} value={value} index={index}>
                {Object.keys(documentProps[tab]).map((section, i) => (
                    <Stack key={section}>
                        {tab === 'Fonts' ? (
                            <Box
                                className={styles.fontsHeader}
                                onClick={() => handleFontsCollapseClick(i)}
                            >
                                <Icon
                                    path={isCollapseOpen[i] ? mdiChevronDown : mdiChevronRight}
                                    size={1}
                                />
                                <Typography component="h6" variant="h6">
                                    {section
                                        ? section.split('_')[0]
                                        : i18next.t('docPropsTable.noEmbeddedFonts')}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography component="h6" variant="h6">
                                {i18next.t(`docPropsTable.sectionHeaders.${section}`)}
                            </Typography>
                        )}
                        <Collapse
                            in={tab !== 'Fonts' ? true : isCollapseOpen[i]}
                            timeout="auto"
                            unmountOnExit
                            sx={{ pl: tab === 'Fonts' ? 4 : 0 }}
                        >
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                <TableBody>
                                    {Object.keys(documentProps[tab][section])
                                        .filter(prop => documentProps[tab][section][prop])
                                        .map(prop => (
                                            <TableRow key={`${section}_${prop}`}>
                                                <TableCell sx={{ px: 0, width: '40%', border: 0 }}>
                                                    {i18next.t(`docPropsTable.metadata.${prop}`)}:
                                                </TableCell>
                                                <TableCell
                                                    sx={{ pr: 0, maxHeight: 150, border: 0 }}
                                                >
                                                    {documentProps[tab][section][prop]}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Collapse>
                    </Stack>
                ))}
            </DocPropsTab>
        ));
    };

    return (
        <>
            <Box className={styles.tabsContainer}>
                <Tabs
                    orientation="vertical"
                    value={value}
                    onChange={handleChange}
                    className={styles.tabs}
                >
                    {Object.keys(tabList).map(tab => (
                        <Tab
                            key={tab}
                            icon={<Icon path={tabList[tab]} size={1} />}
                            iconPosition="start"
                            label={tab}
                            className={styles.tab}
                            id={`vertical-tab-${tab}`}
                            aria-controls={`vertical-tabpanel-${tab}`}
                        />
                    ))}
                </Tabs>
                {createDocPropsTabsContent()}
            </Box>
            <Button variant="contained" color="success" onClick={onClose}>
                {i18next.t('generic.button.close')}
            </Button>
        </>
    );
};

export default DocPropsDialog;
